import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { createFilterOptions } from '@mui/material/Autocomplete';
import type { FilterOptionsState } from '@mui/material/useAutocomplete';
import {
  AttributeOption,
  BrandOption,
  contributionPerUnit,
  createEmptySku,
  hasMissingSkuProductData,
  InternalProductOption,
  PurchaseLine as PurchaseLineType,
  PurchaseLineSourceRef,
  lineCostTotal,
  lineRetailTotal,
  lineUnitTotal,
  markupMultiple,
} from '../../types/purchasing';
import {
  lookupExactCatalogProduct,
  searchCatalogProducts,
  fetchCatalogProductPriceSummary,
} from '../../services/purchasingService';
import SizeMatrix from './SizeMatrix';
import ReadinessBadge from './ReadinessBadge';

const numberValue = (raw: string): number | null => {
  if (!raw.trim()) return null;
  const parsed = Number(raw.replace(',', '.'));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

interface PurchaseLineProps {
  line: PurchaseLineType;
  index: number;
  supplierId: number | null;
  brands: BrandOption[];
  internalProducts: InternalProductOption[];
  attribute1Options: AttributeOption[];
  attribute2Options: AttributeOption[];
  attribute3Options: AttributeOption[];
  currency: string;
  deliveryMonths: { value: string; label: string }[];
  readOnly?: boolean;
  onAttribute1Created?: (attribute: AttributeOption) => void | Promise<void>;
  onChange: (line: PurchaseLineType) => void;
  onRemove: () => void;
  onDuplicate: () => void;
}

const PurchaseLine: React.FC<PurchaseLineProps> = ({
  line,
  index,
  supplierId,
  brands,
  internalProducts,
  attribute1Options,
  attribute2Options,
  attribute3Options,
  currency,
  deliveryMonths,
  readOnly = false,
  onAttribute1Created,
  onChange,
  onRemove,
  onDuplicate,
}) => {
  const [collapsed, setCollapsed] = useState(line.collapsed ?? false);
  const [bulkSkippedColors, setBulkSkippedColors] = useState<string[]>([]);
  const [priceConfirmOpen, setPriceConfirmOpen] = useState(false);
  const [priceConfirmLoading, setPriceConfirmLoading] = useState(false);
  const [priceConfirmSummary, setPriceConfirmSummary] = useState<Awaited<
    ReturnType<typeof fetchCatalogProductPriceSummary>
  > | null>(null);
  const [priceConfirmProduct, setPriceConfirmProduct] =
    useState<InternalProductOption | null>(null);
  const priceConfirmRequestIdRef = useRef(0);
  const prevBulkRef = useRef({
    supplierPrice: line.bulkSupplierPrice ?? null,
    priceOrig: line.bulkPriceOrig ?? null,
    price: line.bulkPrice ?? null,
  });

  const hasUserEnteredData = useMemo(() => {
    if (line.articleNumber?.trim()) return true;
    if (line.productName?.trim()) return true;
    if ((line.skus?.length || 0) === 0) return false;

    return line.skus.some((sku) => {
      if (Number(sku.quantity) > 0) return true;
      if (sku.pluNo?.trim()) return true;
      if (sku.ean13?.trim()) return true;
      if (sku.articleName?.trim()) return true;
      if (sku.attribute1Code || sku.attribute2Code || sku.attribute3Code)
        return true;
      if (
        sku.supplierPrice != null ||
        sku.priceOrig != null ||
        sku.price != null
      )
        return true;
      if (sku.weight != null) return true;
      return false;
    });
  }, [line.articleNumber, line.productName, line.skus]);

  const units = lineUnitTotal(line);
  const cost = lineCostTotal(line);
  const retail = lineRetailTotal(line);
  const hasCampaignPrice = line.bulkPrice != null;
  const effectiveBulkPrice = line.bulkPrice ?? line.bulkPriceOrig ?? null;
  const isSystemMatched = useMemo(
    () => line.skus?.some((s) => Boolean(s.sourceSkuId)) ?? false,
    [line.skus],
  );
  const isNewProductLine = line.source === 'manual' && !isSystemMatched;
  const showProductNameError =
    isNewProductLine && !(line.productName || '').trim();
  const productWeightState = useMemo(() => {
    if ((line.skus?.length || 0) === 0) {
      return { value: null as number | null, mixed: false };
    }

    const weights = line.skus.map((sku) => {
      const weight = Number(sku.weight);
      return Number.isFinite(weight) && weight > 0 ? weight : null;
    });
    const first = weights[0] ?? null;
    const mixed = weights.some((weight) => weight !== first);

    return {
      value: mixed ? null : first,
      mixed,
    };
  }, [line.skus]);

  const setProductWeight = (raw: string) => {
    const weight = numberValue(raw);
    onChange({
      ...line,
      skus: line.skus.map((sku) => ({
        ...sku,
        weight,
      })),
    });
  };

  const handleRemove = () => {
    const label = line.productName?.trim()
      ? line.productName.trim()
      : line.articleNumber?.trim()
        ? line.articleNumber.trim()
        : `rad ${index + 1}`;

    const confirmed = window.confirm(
      `Ta bort ${label} från inköpsordern?\n\nDet går inte att ångra.`,
    );
    if (!confirmed) return;
    onRemove();
  };

  // Use bulk prices for line-level display when no SKU prices set yet
  const displayCost =
    cost > 0 ? cost / Math.max(units, 1) : (line.bulkSupplierPrice ?? 0);
  const displayRetail =
    retail > 0 ? retail / Math.max(units, 1) : (effectiveBulkPrice ?? 0);
  const markup = markupMultiple(displayCost, displayRetail);
  const tb = contributionPerUnit(displayCost, displayRetail);
  const priceWarning = useMemo(() => {
    const fallbackCost = line.bulkSupplierPrice ?? null;
    const fallbackRetail = effectiveBulkPrice ?? null;

    return line.skus.some((sku) => {
      const skuCost = sku.supplierPrice ?? fallbackCost;
      const skuRetail = sku.price ?? fallbackRetail;
      if (!skuCost || !skuRetail) return false;
      return skuRetail <= skuCost;
    });
  }, [effectiveBulkPrice, line.bulkSupplierPrice, line.skus]);
  const lowMarkup = markup !== null && markup < 2.0;

  const hasMissingProductData = line.skus.some(hasMissingSkuProductData);

  const [productQuery, setProductQuery] = useState('');
  const [catalogProducts, setCatalogProducts] = useState<
    InternalProductOption[]
  >([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const catalogRequestIdRef = useRef(0);

  const [exactLookup, setExactLookup] = useState<{
    status: 'idle' | 'loading' | 'done';
    input: string;
    result:
      | null
      | {
          exists: false;
          productNumber: string;
        }
      | {
          exists: true;
          productId: number;
          productNumber: string;
          productName: string | null;
          brandName: string | null;
          supplierId: number | null;
          supplierName: string | null;
        };
  }>({ status: 'idle', input: '', result: null });
  const exactRequestIdRef = useRef(0);

  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [pendingInternalProduct, setPendingInternalProduct] =
    useState<InternalProductOption | null>(null);
  const [selectedColorCodes, setSelectedColorCodes] = useState<string[]>([]);

  const lineSourceRef = useMemo(
    () => (line.sourceRef || null) as PurchaseLineSourceRef | null,
    [line.sourceRef],
  );
  const isImportedFromExcel = lineSourceRef?.importedFromExcel === true;
  const productMetadataLocked = lineSourceRef?.productMetadataLocked === true;
  const productMetadataLockedHelperText =
    'Hämtat från befintlig artikel i systemet.';

  useEffect(() => {
    if (!supplierId || supplierId <= 0) {
      setExactLookup({ status: 'idle', input: '', result: null });
      return;
    }

    const input = productQuery.trim();
    if (!input) {
      setExactLookup({ status: 'idle', input: '', result: null });
      return;
    }

    // Avoid spamming backend for very short inputs.
    if (input.length < 3) {
      setExactLookup({ status: 'idle', input, result: null });
      return;
    }

    const requestId = ++exactRequestIdRef.current;
    setExactLookup((cur) => ({ ...cur, status: 'loading', input }));
    const timer = window.setTimeout(async () => {
      try {
        const result = await lookupExactCatalogProduct(input);
        if (exactRequestIdRef.current === requestId) {
          setExactLookup({ status: 'done', input, result });
        }
      } catch {
        if (exactRequestIdRef.current === requestId) {
          // Fail open: allow "Ny produkt" if exact lookup fails.
          setExactLookup({ status: 'done', input, result: null });
        }
      }
    }, 250);

    return () => window.clearTimeout(timer);
  }, [supplierId, productQuery]);

  useEffect(() => {
    if (!supplierId || supplierId <= 0) {
      setCatalogProducts([]);
      setCatalogLoading(false);
      return;
    }

    const q = productQuery.trim();
    const brand = (line.brandName || '').trim() || undefined;

    // Debounce to avoid spamming API on every keystroke.
    const requestId = ++catalogRequestIdRef.current;
    setCatalogLoading(true);
    const timer = window.setTimeout(async () => {
      try {
        const products = await searchCatalogProducts({
          supplierId,
          q: q || undefined,
          brand,
          limit: 50,
        });
        if (catalogRequestIdRef.current === requestId) {
          setCatalogProducts(products);
        }
      } catch (error) {
        // Keep previous list on error, but stop the spinner.
      } finally {
        if (catalogRequestIdRef.current === requestId) {
          setCatalogLoading(false);
        }
      }
    }, 250);

    return () => {
      window.clearTimeout(timer);
    };
  }, [supplierId, productQuery, line.brandName]);

  const currentInternalProductId = useMemo(() => {
    const productIdRaw = lineSourceRef?.productId;
    if (typeof productIdRaw === 'number' && Number.isFinite(productIdRaw)) {
      return productIdRaw;
    }
    if (typeof productIdRaw === 'string') {
      const n = Number(productIdRaw);
      return Number.isFinite(n) ? n : null;
    }
    return null;
  }, [lineSourceRef]);

  const currentInternalProductNumber = useMemo(() => {
    if (line.source !== 'internal') return null;
    const n = (line.articleNumber || '').trim();
    return n ? n : null;
  }, [line.articleNumber, line.source]);

  const openColorPickerForInternalProduct = (
    product: InternalProductOption,
  ) => {
    setPendingInternalProduct(product);
    // Start with none selected, to avoid importing irrelevant existing colors.
    setSelectedColorCodes([]);
    setColorPickerOpen(true);
  };

  const fmtRange = (min: number | null, max: number | null) => {
    const minN = typeof min === 'number' ? min : null;
    const maxN = typeof max === 'number' ? max : null;
    if (minN == null && maxN == null) return '—';
    if (minN != null && maxN != null && minN === maxN) return fmt(minN);
    if (minN != null && maxN != null) return `${fmt(minN)} – ${fmt(maxN)}`;
    return fmt(minN ?? maxN ?? 0);
  };

  const handleInternalProductChosen = async (
    product: InternalProductOption,
  ) => {
    if (readOnly) return;
    if (isImportedFromExcel) {
      openColorPickerForInternalProduct(product);
      return;
    }

    const requestId = ++priceConfirmRequestIdRef.current;
    setPriceConfirmLoading(true);
    setPriceConfirmProduct(product);
    setPriceConfirmSummary(null);

    try {
      const summary = await fetchCatalogProductPriceSummary(
        product.productNumber,
      );
      if (priceConfirmRequestIdRef.current !== requestId) return;

      // If no system prices exist, just continue without prompting.
      if (!summary || summary.available !== true) {
        setPriceConfirmOpen(false);
        openColorPickerForInternalProduct(product);
        return;
      }

      setPriceConfirmSummary(summary);
      setPriceConfirmOpen(true);
    } catch {
      // Fail open: don't block product selection if price lookup fails.
      if (priceConfirmRequestIdRef.current !== requestId) return;
      setPriceConfirmOpen(false);
      openColorPickerForInternalProduct(product);
    } finally {
      if (priceConfirmRequestIdRef.current === requestId) {
        setPriceConfirmLoading(false);
      }
    }
  };

  const closePriceConfirm = () => {
    setPriceConfirmOpen(false);
    setPriceConfirmSummary(null);
    setPriceConfirmProduct(null);
    setPriceConfirmLoading(false);
  };

  const applySystemPricesAndContinue = () => {
    const product = priceConfirmProduct;
    const summary = priceConfirmSummary;
    if (!product || !summary || summary.available !== true) {
      closePriceConfirm();
      return;
    }

    const supplierPrice =
      summary.supplierPrice.distinctCount === 1
        ? summary.supplierPrice.min
        : null;
    const priceOrig =
      summary.priceOrig.distinctCount === 1 ? summary.priceOrig.min : null;
    const bulkPrice = priceOrig != null ? priceOrig : null;

    onChange({
      ...line,
      bulkSupplierPrice: supplierPrice,
      bulkPriceOrig: priceOrig,
      bulkPrice,
    });

    closePriceConfirm();
    openColorPickerForInternalProduct(product);
  };

  const keepOrderPricesAndContinue = () => {
    const product = priceConfirmProduct;
    if (!product) {
      closePriceConfirm();
      return;
    }
    closePriceConfirm();
    openColorPickerForInternalProduct(product);
  };

  const applyInternalProductSelection = (
    product: InternalProductOption,
    includeColorCodes: string[],
  ) => {
    const selectedProductWeight = productWeightState.mixed
      ? null
      : productWeightState.value;
    const includeSet = new Set(includeColorCodes);
    const filteredSkus = product.skus.filter((sku) => {
      const code = (sku.attribute1Code || '').trim();
      if (!code) return includeSet.has('');
      return includeSet.has(code);
    });

    const nextSkus =
      filteredSkus.length > 0
        ? filteredSkus.map((sku) => ({
            articleNumber: product.productNumber,
            pluNo: sku.priceLookupUnitNumber,
            attribute1Code: sku.attribute1Code,
            attribute2Code: sku.attribute2Code,
            attribute3Code: sku.attribute3Code,
            ean13: sku.europeanArticleNumber,
            articleName: product.productName,
            supplierPrice: line.bulkSupplierPrice ?? null,
            priceOrig: line.bulkPriceOrig ?? null,
            price: effectiveBulkPrice ?? null,
            weight: selectedProductWeight ?? sku.weight,
            quantity: 0,
            sourceSkuId: sku.skuId,
          }))
        : [
            {
              ...createEmptySku(product.productNumber, product.productName),
              weight: selectedProductWeight,
            },
          ];

    onChange({
      ...line,
      articleNumber: product.productNumber,
      productName: product.productName,
      brandName: product.brandName,
      source: 'internal',
      sourceRef: { productId: product.productId },
      skus: nextSkus,
    });
  };

  type ProductPickerOption =
    | {
        kind: 'existing';
        source: 'internal' | 'supplier_api';
        product: InternalProductOption;
      }
    | {
        kind: 'new';
        inputValue?: string;
      };

  const productOptions = useMemo<ProductPickerOption[]>(() => {
    const existing: ProductPickerOption[] = catalogProducts.map((product) => ({
      kind: 'existing',
      source: 'internal',
      product,
    }));

    // Always include a fallback option for current selection so value renders
    // even if it isn't included in the current search result set.
    const hasCurrent =
      (currentInternalProductId != null ||
        currentInternalProductNumber != null) &&
      existing.some(
        (o) =>
          o.kind === 'existing' &&
          (currentInternalProductId != null
            ? o.product.productId === currentInternalProductId
            : o.product.productNumber === currentInternalProductNumber),
      );

    const withCurrent =
      line.source === 'internal' &&
      (currentInternalProductId != null ||
        currentInternalProductNumber != null) &&
      line.articleNumber?.trim() &&
      line.productName?.trim() &&
      !hasCurrent
        ? ([
            {
              kind: 'existing',
              source: 'internal',
              product: {
                // productId is used for equality when present; fallback uses productNumber.
                productId: currentInternalProductId ?? -1,
                productNumber: line.articleNumber,
                productName: line.productName,
                brandName: line.brandName ?? null,
                skus: [],
              },
            } satisfies ProductPickerOption,
            ...existing,
          ] as ProductPickerOption[])
        : existing;

    const canOfferNewProduct =
      exactLookup.status !== 'loading' &&
      !(exactLookup.result && exactLookup.result.exists === true);

    return canOfferNewProduct ? [...withCurrent, { kind: 'new' }] : withCurrent;
  }, [
    catalogProducts,
    line.articleNumber,
    line.brandName,
    line.productName,
    line.source,
    currentInternalProductId,
    currentInternalProductNumber,
    exactLookup.status,
    exactLookup.result,
  ]);

  const selectedProductOption = useMemo<ProductPickerOption | null>(() => {
    if (line.source !== 'internal') return null;
    const canMatchById = currentInternalProductId != null;
    const canMatchByNumber = currentInternalProductNumber != null;
    if (!canMatchById && !canMatchByNumber) return null;

    const fromOptions =
      productOptions.find(
        (o) =>
          o.kind === 'existing' &&
          o.source === 'internal' &&
          (canMatchById
            ? o.product.productId === currentInternalProductId
            : o.product.productNumber === currentInternalProductNumber),
      ) || null;

    if (fromOptions) return fromOptions;
    if (!line.articleNumber?.trim() || !line.productName?.trim()) return null;

    // Last-resort fallback: render saved value even if neither the search result
    // nor the injected fallback option exists yet.
    return {
      kind: 'existing',
      source: 'internal',
      product: {
        productId: currentInternalProductId ?? -1,
        productNumber: line.articleNumber,
        productName: line.productName,
        brandName: line.brandName ?? null,
        skus: [],
      },
    };
  }, [
    currentInternalProductId,
    currentInternalProductNumber,
    line.articleNumber,
    line.brandName,
    line.productName,
    line.source,
    productOptions,
  ]);

  const filterProductOptions = useMemo(() => {
    const filter = createFilterOptions<ProductPickerOption>({
      stringify: (o) =>
        o.kind === 'existing'
          ? `${o.product.productNumber} ${o.product.productName} ${o.product.brandName || ''}`
          : 'Ny produkt',
    });

    return (
      options: ProductPickerOption[],
      params: FilterOptionsState<ProductPickerOption>,
    ) => {
      const allowNew =
        exactLookup.status !== 'loading' &&
        !(exactLookup.result && exactLookup.result.exists === true);

      const filtered = filter(options, params).filter(
        (o) => allowNew || o.kind !== 'new',
      );
      const inputValue = params.inputValue?.trim() || '';

      return allowNew
        ? ([
            ...filtered,
            {
              kind: 'new',
              inputValue: inputValue || undefined,
            } satisfies ProductPickerOption,
          ] as ProductPickerOption[])
        : filtered;
    };
  }, [exactLookup.status, exactLookup.result]);

  const exactMatchInfo = useMemo(() => {
    const r = exactLookup.result;
    if (!r || r.exists !== true) return null;
    const usedBy = r.supplierName || r.brandName || 'okänd';
    const label = r.productName
      ? `${r.productNumber} · ${r.productName}`
      : r.productNumber;
    return `Artikelnummer används av ${usedBy} (${label})`;
  }, [exactLookup.result]);

  useEffect(() => {
    const previous = prevBulkRef.current;
    const current = {
      supplierPrice: line.bulkSupplierPrice ?? null,
      priceOrig: line.bulkPriceOrig ?? null,
      price: line.bulkPrice ?? null,
    };
    const changed =
      previous.supplierPrice !== current.supplierPrice ||
      previous.priceOrig !== current.priceOrig ||
      previous.price !== current.price;

    const manualColors = new Set(line.manualPriceColors || []);
    const skippedColors = Array.from(
      new Set(
        line.skus
          .map((sku) => sku.attribute1Code || '')
          .filter((colorCode) => colorCode && manualColors.has(colorCode)),
      ),
    );
    if (changed) {
      setBulkSkippedColors(skippedColors);
      prevBulkRef.current = current;
    }

    let didChange = false;
    const nextSkus = line.skus.map((sku) => {
      const colorCode = sku.attribute1Code || '';
      if (colorCode && manualColors.has(colorCode)) {
        return sku;
      }

      const nextSku = {
        ...sku,
        supplierPrice: line.bulkSupplierPrice ?? sku.supplierPrice ?? null,
        priceOrig: line.bulkPriceOrig ?? sku.priceOrig ?? null,
        price: effectiveBulkPrice ?? sku.price ?? null,
      };
      if (
        nextSku.supplierPrice !== sku.supplierPrice ||
        nextSku.priceOrig !== sku.priceOrig ||
        nextSku.price !== sku.price
      ) {
        didChange = true;
      }
      return nextSku;
    });

    if (didChange) {
      onChange({
        ...line,
        skus: nextSkus,
      });
    }
  }, [
    line.bulkSupplierPrice,
    line.bulkPriceOrig,
    line.bulkPrice,
    line.skus,
    line.manualPriceColors,
  ]);

  // Note: internal product selection now uses a color-picker dialog before
  // importing SKUs, to avoid pulling in irrelevant colors.

  const fmt = (value: number) =>
    new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(value || 0);

  const productSelectionPending =
    !readOnly &&
    !isSystemMatched &&
    line.source !== 'internal' &&
    !line.articleNumber?.trim() &&
    !line.productName?.trim();

  const colorLabel = (code: string) =>
    attribute1Options.find((option) => option.code === code)?.code || code;

  return (
    <Card sx={{ border: '1px solid', borderColor: 'divider' }}>
      <Dialog
        open={priceConfirmOpen}
        onClose={keepOrderPricesAndContinue}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Systempriser hittade</DialogTitle>
        <DialogContent>
          <Stack spacing={1.25} sx={{ mt: 1 }}>
            <Typography variant="body2">
              Inköpspris och försäljningspris finns sparade i systemet för{' '}
              <strong>{priceConfirmProduct?.productNumber}</strong>. Stämmer
              det, eller vill du behålla priserna på ordern?
            </Typography>

            {priceConfirmSummary && priceConfirmSummary.available === true ? (
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1.5,
                  p: 1.25,
                }}
              >
                <Stack spacing={0.75}>
                  <Stack direction="row" justifyContent="space-between" gap={2}>
                    <Typography variant="body2" color="text.secondary">
                      Systemets inköpspris
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {fmtRange(
                        priceConfirmSummary.supplierPrice.min,
                        priceConfirmSummary.supplierPrice.max,
                      )}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" gap={2}>
                    <Typography variant="body2" color="text.secondary">
                      Systemets försäljningspris (RRP)
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {fmtRange(
                        priceConfirmSummary.priceOrig.min,
                        priceConfirmSummary.priceOrig.max,
                      )}
                    </Typography>
                  </Stack>
                  {(priceConfirmSummary.supplierPrice.distinctCount > 1 ||
                    priceConfirmSummary.priceOrig.distinctCount > 1) && (
                    <Typography variant="caption" color="text.secondary">
                      Priserna varierar mellan varianter. “Uppdatera” fyller
                      bara i systempriser om det finns ett entydigt pris.
                    </Typography>
                  )}
                </Stack>
              </Box>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={keepOrderPricesAndContinue} variant="outlined">
            Sätt pris manuellt
          </Button>
          <Button
            onClick={applySystemPricesAndContinue}
            variant="contained"
            disabled={priceConfirmLoading}
          >
            Uppdatera med systempriser
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Header ──────────────────────────────────────────── */}
      <CardHeader
        sx={{
          bgcolor: 'grey.50',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
        title={
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            flexWrap="wrap"
          >
            <Chip label={`#${index + 1}`} size="small" />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {line.articleNumber || `Produkt ${index + 1}`}
            </Typography>
            {line.productName && (
              <Typography variant="body2" color="text.secondary">
                {line.productName}
              </Typography>
            )}
            <ReadinessBadge
              ready={Boolean(line.readyForTransfer)}
              missingFields={line.missingFields}
            />
            {hasMissingProductData && (
              <Chip
                size="small"
                color="warning"
                variant="outlined"
                label="Saknar produktdata"
              />
            )}
            {productMetadataLocked && (
              <Chip
                size="small"
                color="info"
                variant="outlined"
                label="Systemnamn låst"
              />
            )}
          </Stack>
        }
        action={
          <Stack direction="row" spacing={0.5} alignItems="center">
            {/* Mini stats */}
            <Box sx={{ textAlign: 'right', pr: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{
                  lineHeight: 1,
                  fontSize: 10,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                }}
              >
                Antal
              </Typography>
              <Typography variant="body2" fontWeight={700}>
                {units}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right', pr: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{
                  lineHeight: 1,
                  fontSize: 10,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                }}
              >
                Inköp
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {fmt(cost)}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right', pr: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{
                  lineHeight: 1,
                  fontSize: 10,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                }}
              >
                Markup
              </Typography>
              <Typography
                variant="body2"
                fontWeight={600}
                color={
                  priceWarning
                    ? 'error'
                    : lowMarkup
                      ? 'warning.main'
                      : 'text.primary'
                }
              >
                {markup !== null ? `${markup.toFixed(1)}×` : '–'}
              </Typography>
            </Box>
            <IconButton onClick={() => setCollapsed((p) => !p)} size="small">
              {collapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
            </IconButton>
            <IconButton onClick={onDuplicate} size="small" disabled={readOnly}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
            <IconButton onClick={handleRemove} size="small" disabled={readOnly}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Stack>
        }
      />

      {/* ── Expanded body ────────────────────────────────────── */}
      <Collapse in={!collapsed}>
        <CardContent>
          <Grid container spacing={2}>
            {/* Product + Brand + Delivery month */}
            <Grid item xs={12} md={4}>
              <Autocomplete
                size="small"
                options={productOptions}
                value={selectedProductOption}
                filterOptions={filterProductOptions}
                loading={catalogLoading}
                getOptionLabel={(o) => {
                  if (o.kind === 'new') {
                    return o.inputValue
                      ? `Ny produkt: ${o.inputValue}`
                      : 'Ny produkt';
                  }
                  return `${o.product.productNumber} · ${o.product.productName}`;
                }}
                onInputChange={(_, next, reason) => {
                  // Let Autocomplete manage the visible input text so the
                  // saved/selected product label renders immediately.
                  if (reason === 'input') setProductQuery(next);
                  if (reason === 'clear') setProductQuery('');
                }}
                isOptionEqualToValue={(a, b) => {
                  if (a.kind !== b.kind) return false;
                  if (a.kind === 'new') {
                    if (b.kind !== 'new') return false;
                    return (a.inputValue || '') === (b.inputValue || '');
                  }
                  if (b.kind === 'new') return false;
                  // Prefer stable internal id; fall back to productNumber when id is missing/unset.
                  const aId = a.product.productId;
                  const bId = b.product.productId;
                  if (aId > 0 && bId > 0) return aId === bId;
                  return a.product.productNumber === b.product.productNumber;
                }}
                onChange={(_, option) => {
                  if (!option) return;

                  if (option.kind === 'new') {
                    const typed = option.inputValue?.trim() || '';
                    onChange({
                      ...line,
                      source: 'manual',
                      sourceRef: null,
                      // Keep what user already typed on the line.
                      brandName: line.brandName ?? null,
                      articleNumber:
                        (line.articleNumber || '').trim() ||
                        (typed ? typed : ''),
                      productName: line.productName || '',
                      skus:
                        (line.skus?.length || 0) > 0
                          ? line.skus
                          : [createEmptySku()],
                    });
                    return;
                  }

                  if (option.source === 'internal') {
                    void handleInternalProductChosen(option.product);
                    return;
                  }
                }}
                renderOption={(props, option) => {
                  if (option.kind === 'new') {
                    return (
                      <li {...props} key="new-product">
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ width: '100%' }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {option.inputValue
                              ? `Ny produkt: ${option.inputValue}`
                              : 'Ny produkt'}
                          </Typography>
                          <Chip
                            size="small"
                            label="Manuell"
                            variant="outlined"
                          />
                        </Stack>
                      </li>
                    );
                  }

                  const sourceLabel =
                    option.source === 'internal' ? 'Intern' : 'Leverantör';

                  return (
                    <li
                      {...props}
                      key={`p-${option.source}-${option.product.productId}`}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ width: '100%' }}
                      >
                        <Typography variant="body2">
                          {option.product.productNumber} ·{' '}
                          {option.product.productName}
                        </Typography>
                        <Chip
                          size="small"
                          label={sourceLabel}
                          variant="outlined"
                        />
                      </Stack>
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Produkt"
                    placeholder="Sök intern produkt eller välj Ny produkt"
                    helperText={
                      productMetadataLocked
                        ? productMetadataLockedHelperText
                        : exactMatchInfo || ' '
                    }
                  />
                )}
                disabled={
                  readOnly ||
                  !supplierId ||
                  isSystemMatched ||
                  productMetadataLocked
                }
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Autocomplete
                size="small"
                options={brands}
                value={
                  brands.find((b) => b.brandName === line.brandName) || null
                }
                disabled={
                  readOnly ||
                  !supplierId ||
                  isSystemMatched ||
                  productMetadataLocked ||
                  productSelectionPending
                }
                getOptionLabel={(o) => o.brandName}
                onChange={(_, o) => {
                  const newBrand = o?.brandName || null;
                  if (newBrand === line.brandName) return;
                  // If the currently selected product doesn't belong to the new brand, clear it.
                  //
                  // For manual/"Ny produkt" lines we must NOT clear user input when brand changes.
                  // `internalProducts` is not populated here, so treating manual lines as "belongs"
                  // avoids wiping data unexpectedly.
                  const productBelongsToNewBrand =
                    line.source !== 'internal' ||
                    !newBrand ||
                    !line.articleNumber ||
                    internalProducts.find(
                      (p) =>
                        p.productNumber === line.articleNumber &&
                        p.brandName === newBrand,
                    );

                  const willClearProductData = !productBelongsToNewBrand;
                  if (willClearProductData && hasUserEnteredData) {
                    const confirmed = window.confirm(
                      'Byta varumärke?\n\nDetta rensar artikel, produktnamn och alla ifyllda varianter på raden.',
                    );
                    if (!confirmed) return;
                  }

                  onChange({
                    ...line,
                    brandName: newBrand,
                    articleNumber: productBelongsToNewBrand
                      ? line.articleNumber
                      : '',
                    productName: productBelongsToNewBrand
                      ? line.productName
                      : '',
                    skus: productBelongsToNewBrand ? line.skus : [],
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Varumärke"
                    helperText={
                      productMetadataLocked
                        ? productMetadataLockedHelperText
                        : ' '
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                size="small"
                label="Leveransmånad"
                disabled={readOnly || productSelectionPending}
                value={line.deliveryMonth || ''}
                onChange={(e) =>
                  onChange({ ...line, deliveryMonth: e.target.value || null })
                }
              >
                {deliveryMonths.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Artikelnummer + Produktnamn */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Artikelnummer"
                value={line.articleNumber}
                disabled={
                  readOnly ||
                  productSelectionPending ||
                  line.source === 'internal' ||
                  isSystemMatched ||
                  productMetadataLocked
                }
                helperText={
                  productMetadataLocked ? productMetadataLockedHelperText : ' '
                }
                onChange={(e) =>
                  onChange({ ...line, articleNumber: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                size="small"
                label="Produktnamn"
                value={line.productName || ''}
                disabled={
                  readOnly ||
                  productSelectionPending ||
                  line.source === 'internal' ||
                  isSystemMatched ||
                  productMetadataLocked
                }
                error={showProductNameError}
                helperText={
                  productMetadataLocked
                    ? productMetadataLockedHelperText
                    : showProductNameError
                      ? 'Produktnamn krävs för Ny produkt'
                      : ' '
                }
                onChange={(e) =>
                  onChange({ ...line, productName: e.target.value })
                }
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Vikt (produkt)"
                type="number"
                value={
                  productWeightState.mixed
                    ? ''
                    : (productWeightState.value ?? '')
                }
                disabled={readOnly || productSelectionPending}
                placeholder={productWeightState.mixed ? 'Blandade vikter' : ''}
                helperText={
                  productWeightState.mixed
                    ? 'Blandade vikter - ange värde för att uppdatera alla'
                    : 'Krävs när ordern skapas'
                }
                inputProps={{
                  min: 0,
                  step: '0.001',
                  style: { textAlign: 'right' },
                }}
                onChange={(e) => setProductWeight(e.target.value)}
              />
            </Grid>

            {/* Pricing row: Inpris | Ordinarie pris | Kampanj */}
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Inpris"
                type="number"
                value={line.bulkSupplierPrice ?? ''}
                disabled={readOnly || productSelectionPending}
                inputProps={{
                  min: 0,
                  step: '0.01',
                  style: { textAlign: 'right' },
                }}
                InputProps={{
                  endAdornment: (
                    <span style={{ fontSize: 11, color: '#888' }}>
                      {currency}
                    </span>
                  ),
                }}
                onChange={(e) =>
                  onChange({
                    ...line,
                    bulkSupplierPrice: e.target.value
                      ? Number(e.target.value)
                      : null,
                  })
                }
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Ordinarie pris"
                type="number"
                value={line.bulkPriceOrig ?? ''}
                disabled={readOnly || productSelectionPending}
                inputProps={{
                  min: 0,
                  step: '0.01',
                  style: { textAlign: 'right' },
                }}
                InputProps={{
                  endAdornment: (
                    <span style={{ fontSize: 11, color: '#888' }}>
                      {currency}
                    </span>
                  ),
                }}
                onChange={(e) =>
                  onChange({
                    ...line,
                    bulkPriceOrig: e.target.value
                      ? Number(e.target.value)
                      : null,
                  })
                }
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={hasCampaignPrice}
                    disabled={readOnly || productSelectionPending}
                    onChange={(_, checked) =>
                      onChange({
                        ...line,
                        bulkPrice: checked
                          ? (line.bulkPriceOrig ?? null)
                          : null,
                      })
                    }
                  />
                }
                label="Kampanj"
              />
            </Grid>
            {hasCampaignPrice && (
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Kampanjpris"
                  type="number"
                  value={line.bulkPrice ?? ''}
                  disabled={readOnly || productSelectionPending}
                  inputProps={{
                    min: 0,
                    step: '0.01',
                    style: { textAlign: 'right' },
                  }}
                  InputProps={{
                    endAdornment: (
                      <span style={{ fontSize: 11, color: '#888' }}>
                        {currency}
                      </span>
                    ),
                  }}
                  sx={
                    priceWarning
                      ? {
                          '& .MuiOutlinedInput-root': {
                            borderColor: 'error.main',
                          },
                        }
                      : {}
                  }
                  onChange={(e) =>
                    onChange({
                      ...line,
                      bulkPrice: e.target.value ? Number(e.target.value) : null,
                    })
                  }
                />
              </Grid>
            )}
            <Grid item xs={6} md={hasCampaignPrice ? 1 : 2}>
              <TextField
                fullWidth
                size="small"
                label="Markup"
                value={markup !== null ? `${markup.toFixed(2)}×` : '–'}
                InputProps={{
                  readOnly: true,
                  sx: {
                    textAlign: 'right',
                    bgcolor: priceWarning
                      ? 'error.50'
                      : lowMarkup
                        ? 'warning.50'
                        : 'grey.50',
                    color: priceWarning
                      ? 'error.main'
                      : lowMarkup
                        ? 'warning.main'
                        : 'text.primary',
                    fontWeight: 600,
                  },
                }}
              />
            </Grid>
            <Grid item xs={6} md={hasCampaignPrice ? 1 : 2}>
              <TextField
                fullWidth
                size="small"
                label="TB / styck"
                value={displayCost > 0 || displayRetail > 0 ? fmt(tb) : '–'}
                InputProps={{
                  readOnly: true,
                  sx: {
                    textAlign: 'right',
                    bgcolor: 'grey.50',
                    fontWeight: 600,
                  },
                }}
              />
            </Grid>
          </Grid>

          {/* Price warning */}
          {priceWarning && (
            <Alert severity="error" icon={<WarningAmberIcon />} sx={{ mt: 2 }}>
              Minst en variant har utpris som är lägre än (eller lika med)
              inpriset. Kontrollera värdena.
            </Alert>
          )}
          {bulkSkippedColors.length > 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Manuellt uppdaterade färger hoppades över vid bulk-uppdatering:{' '}
              <strong>{bulkSkippedColors.map(colorLabel).join(', ')}</strong>
            </Alert>
          )}

          {line.source === 'supplier_api' && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Leverantörs-API kommer i nästa steg. Använd intern eller manuell
              källa i MVP.
            </Alert>
          )}

          <Box sx={{ mt: 2 }}>
            <SizeMatrix
              line={line}
              attribute1Options={attribute1Options}
              attribute2Options={attribute2Options}
              attribute3Options={attribute3Options}
              deliveryMonths={deliveryMonths}
              onChange={onChange}
              onAttribute1Created={onAttribute1Created}
              readOnly={readOnly || productSelectionPending}
            />
          </Box>

          {/* Line totals footer */}
          {units > 0 && (
            <>
              <Divider sx={{ mt: 2 }} />
              <Stack
                direction="row"
                justifyContent="flex-end"
                spacing={3}
                sx={{ pt: 1.5 }}
              >
                <Typography variant="body2" color="text.secondary">
                  {units} st
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Inköp{' '}
                  <strong style={{ color: 'inherit' }}>{fmt(cost)}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Försäljning{' '}
                  <strong style={{ color: 'inherit' }}>{fmt(retail)}</strong>
                </Typography>
                {markup !== null && (
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color={
                      priceWarning
                        ? 'error'
                        : lowMarkup
                          ? 'warning.main'
                          : 'success.main'
                    }
                  >
                    {markup.toFixed(2)}× markup
                  </Typography>
                )}
              </Stack>
            </>
          )}
        </CardContent>
      </Collapse>

      <Dialog
        open={colorPickerOpen}
        onClose={() => {
          setColorPickerOpen(false);
          setPendingInternalProduct(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Välj färger att lägga till</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Produkten kan ha många färger i systemet. Välj bara de färger du
            vill lägga till på raden (om du ska lägga upp en helt ny färg kan du
            avmarkera de andra).
          </Typography>

          {pendingInternalProduct
            ? (() => {
                const codes = Array.from(
                  new Set(
                    pendingInternalProduct.skus
                      .map((s) => (s.attribute1Code || '').trim())
                      .filter(Boolean),
                  ),
                ).sort();

                return (
                  <Stack spacing={0.5}>
                    <Stack direction="row" spacing={1} sx={{ mb: 0.5 }}>
                      <Button
                        size="small"
                        onClick={() => setSelectedColorCodes(codes)}
                      >
                        Markera alla
                      </Button>
                      <Button
                        size="small"
                        onClick={() => setSelectedColorCodes([])}
                      >
                        Avmarkera alla
                      </Button>
                    </Stack>

                    {codes.length === 0 ? (
                      <Alert severity="info">
                        Inga färger hittades på produkten. Vi skapar en tom rad
                        så kan du lägga till nya färger manuellt.
                      </Alert>
                    ) : (
                      codes.map((code) => {
                        const label =
                          attribute1Options.find((o) => o.code === code)
                            ?.code || code;
                        const checked = selectedColorCodes.includes(code);
                        return (
                          <FormControlLabel
                            key={code}
                            control={
                              <Checkbox
                                checked={checked}
                                onChange={(_, next) => {
                                  setSelectedColorCodes((cur) =>
                                    next
                                      ? Array.from(new Set([...cur, code]))
                                      : cur.filter((c) => c !== code),
                                  );
                                }}
                              />
                            }
                            label={label}
                          />
                        );
                      })
                    )}
                  </Stack>
                );
              })()
            : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setColorPickerOpen(false);
              setPendingInternalProduct(null);
            }}
          >
            Avbryt
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (!pendingInternalProduct) return;
              applyInternalProductSelection(
                pendingInternalProduct,
                selectedColorCodes,
              );
              setColorPickerOpen(false);
              setPendingInternalProduct(null);
            }}
          >
            Lägg till
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default PurchaseLine;
